import React, { createContext, useContext, useMemo, useState, useEffect, useRef } from 'react'
import { useAuthDataContext } from './Auth'
import Api from '../_helpers/Api'
import { useSocketContext } from './Socket'
import sound from '../assets/voice-2.ogg'

export const MessagesContext = createContext({})

let whispers = {}
let whispersTimeout = {}

const MessagesProvider = props => {

    const auth = useAuthDataContext()
    const listener = useSocketContext()
    const userId = auth.id()
    const userType = auth.type()
    const partnerId = auth.partnerId()

    const audio = useRef(new Audio(sound))

    const [state, setState] = useState({
        fromSocket: [],
        read: [],
        whispers: {},
        openChats: [],
        count: 0,
        lastMessage: null,
    })

    const loadCount = (syncTabs = true) => {
        Api.get('messages/total')
            .then(res => {
                setCount(res.data)

                if (syncTabs) {
                    localStorage.setItem('Messages_loadCount', res.data)
                }
            })
    }

    const setCount = count => {
        // setState(prev => ({
        //     ...prev,
        //     count: count
        // }))
    }

    const decrementCount = () => {
        setState(prev => ({
            ...prev,
            count: prev.count > 1 ? prev.count - 1 : 0
        }))
    }

    const clear = () => {
        setState(prev => ({
            ...prev,
            fromSocket: [],
            count: 0,
        }))
    }

    // Accept full user object for chat
    const openChat = (userObj) => {
        if (!auth.access('livechat')) {
            return
        }
        // Use id, type, reservationId, tenant_id, name, lastname, etc.
        const { id, type, reservationId = '', tenant_id, name, lastname } = userObj
        let fullUserId = id + '.' + type + '.' + (reservationId || '')

        let openChats = [...state.openChats]
        // Remove only if exact same conversation (same person and reservation)
        openChats = openChats.filter(c => !(c.fullId === fullUserId))
        // Limit to 3 open chats
        if (openChats.length > 2) {
            openChats.pop()
        }
        openChats = [...openChats, userObj]
        setState(prev => ({
            ...prev,
            openChats: openChats
        }))
    }

    const closeChat = id => {
        let filtered = state.openChats.filter(c => {
            return c.chatId !== id
        })

        setState(prev => ({
            ...prev,
            openChats: filtered
        }))
    }

    const whisper = (id, type, user) => {
        listener.private(`whispers.${id}.${type}`)
            .whisper('typing', {
                user: user,
            })
    }

    const setLastMessage = msg => {
        setState(prev => ({
            ...prev,
            lastMessage: msg
        }))
    }

    const onStorageUpdate = e => {
        const { key, newValue } = e

        // console.log(key, newValue);

        if (key === 'Messages_loadCount') {
            setCount(newValue)
        }
    }

    useEffect(() => {
        console.log('[MessagesProvider] useEffect deps:', { partnerId, userId, userType, partnerId, listener })
        if (!userId || !userType || !listener || !partnerId) return

        loadCount()

        const messageChannel = listener.private(`messages.${partnerId}.${userId}.${userType}`)
            .listen('MessageReceived', message => {
                console.log('[MessagesProvider] MessageReceived event:', message)
                if (message && message.author) {
                    openChat({
                        avatar: message.author.avatar,
                        id: message.author.id,
                        type: message.author.type,
                        name: message.author.name,
                        lastname: message.author.lastname,
                        tenant_id: message.author.receiver_tenant_id,
                        chatId: message.coversation_id,
                        fullId: `${partnerId}.${userId}.${userType}`
                    })
                }
                setState(prev => ({
                    ...prev,
                    fromSocket: prev.fromSocket.concat(message),
                }))
                loadCount()
                audio.current.play().catch(() => { })
            })
            .listen('MessageRead', message => {
                console.log('[MessagesProvider] MessageRead event:', message)
                setState(prev => ({
                    ...prev,
                    read: prev.read.concat(message),
                }))
            })

        let reservationChannel
        if (partnerId !== undefined && partnerId !== null) {
            console.log('[MessagesProvider] Subscribing to reservation-messages channel:', `reservation-messages.${partnerId}`)
            reservationChannel = listener.private(`reservation-messages.${partnerId}`)
                .listen('MessageReceived', message => {
                    console.log('[MessagesProvider] Reservation MessageReceived event:', message)
                    let shouldReceive
                    if (message.receiver) {
                        shouldReceive = Number(message.receiver.id) === Number(userId) && message.receiver.type === userType
                    } else {
                        shouldReceive = true
                    }
                    if (shouldReceive && message && message.author) {
                        openChat(message.author.id, message.author.type)
                    }
                    setState(prev => ({
                        ...prev,
                        fromSocket: prev.fromSocket.concat(message),
                    }))
                    if (shouldReceive) {
                        loadCount()
                        audio.current.play().catch(() => { })
                    }
                })
        } else {
            console.warn('[MessagesProvider] partnerId is undefined or null, not subscribing to reservation-messages channel')
        }

        const whisperChannel = listener.private(`whispers.${userId}.${userType}`)
            .listenForWhisper('typing', e => {
                console.log('[MessagesProvider] Whisper typing event:', e)
                let id = e.user.id + '.' + e.user.type
                whispers[id] = e.user
                setState(prev => ({
                    ...prev,
                    whispers: whispers
                }))
                if (whispersTimeout[id]) {
                    clearTimeout(whispersTimeout[id])
                }
                whispersTimeout[id] = setTimeout(() => {
                    delete whispers[id]
                    setState(prev => ({
                        ...prev,
                        whispers: whispers
                    }))
                }, 1000)
            })

        // Cleanup listeners on dependency change or unmount
        return () => {
            console.log('[MessagesProvider] Cleaning up listeners')
            messageChannel.stopListening('MessageReceived')
            messageChannel.stopListening('MessageRead')
            if (reservationChannel) reservationChannel.stopListening('MessageReceived')
            whisperChannel.stopListeningForWhisper('typing')
        }
    }, [userId, userType, partnerId, listener])

    useEffect(() => {
        window.addEventListener("storage", onStorageUpdate)
        return () => {
            window.removeEventListener("storage", onStorageUpdate)
        }
    }, [])

    const data = useMemo(() => ({
        ...state, loadCount, decrementCount, whisper, clear, openChat, closeChat, setLastMessage
    }), [state])

    return <MessagesContext.Provider value={data} {...props} />
}

export const useMessagesContext = () => useContext(MessagesContext)

export default MessagesProvider